import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Box, Flex, Heading, Text, TextField, Switch, LoadingOrbit, Gear } from '@chatsdet/ui';
import { MagnifyingGlass } from '@phosphor-icons/react';
import { API_URL } from '../../../config.js';
import { fetchFeatureFlags } from './featureFlagsApi.js';

type FeatureFlag = {
  id: string;
  name: string;
  description: string | null;
  enabled: boolean;
};

type FlagGroup = {
  namespace: string;
  flags: FeatureFlag[];
};

const GENERAL_NAMESPACE = 'General';

function formatNamespace(raw: string): string {
  if (!raw) return GENERAL_NAMESPACE;
  return raw
    .split(/[._-]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

function formatFlagLabel(name: string): string {
  const afterDot = name.includes('.') ? name.slice(name.indexOf('.') + 1) : name;
  return afterDot
    .split(/[_-]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

function groupFlags(flags: FeatureFlag[]): FlagGroup[] {
  const map = new Map<string, FeatureFlag[]>();
  for (const flag of flags) {
    const ns = flag.name.includes('.')
      ? formatNamespace(flag.name.slice(0, flag.name.indexOf('.')))
      : GENERAL_NAMESPACE;
    const list = map.get(ns) ?? [];
    list.push(flag);
    map.set(ns, list);
  }
  return Array.from(map.entries())
    .map(([namespace, flags]) => ({
      namespace,
      flags: [...flags].sort((a, b) => a.name.localeCompare(b.name)),
    }))
    .sort((a, b) => {
      if (a.namespace === GENERAL_NAMESPACE) return -1;
      if (b.namespace === GENERAL_NAMESPACE) return 1;
      return a.namespace.localeCompare(b.namespace);
    });
}

export function FeatureFlagsSection() {
  const qc = useQueryClient();
  const [query, setQuery] = React.useState('');

  const featureFlagsQuery = useQuery({
    queryKey: ['feature-flags'],
    queryFn: () => fetchFeatureFlags(),
  });

  const toggleFeatureFlagMutation = useMutation({
    mutationFn: async (flagName: string) => {
      const res = await fetch(`${API_URL}/feature-flags/${flagName}/toggle`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    onMutate: async (flagName) => {
      await qc.cancelQueries({ queryKey: ['feature-flags'] });
      const prev = qc.getQueryData<{ flags: FeatureFlag[] }>(['feature-flags']);
      if (prev) {
        qc.setQueryData<{ flags: FeatureFlag[] }>(['feature-flags'], {
          flags: prev.flags.map((f) =>
            f.name === flagName ? { ...f, enabled: !f.enabled } : f
          ),
        });
      }
      return { prev };
    },
    onError: (_err, _flagName, context) => {
      if (context?.prev) {
        qc.setQueryData(['feature-flags'], context.prev);
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['feature-flags'] });
      qc.invalidateQueries({ queryKey: ['me'] });
    },
  });

  const flags = featureFlagsQuery.data?.flags ?? [];
  const enabledCount = flags.filter((f) => f.enabled).length;

  const filteredFlags = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return flags;
    return flags.filter((f) => {
      const hay = `${f.name} ${formatFlagLabel(f.name)} ${f.description ?? ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [flags, query]);

  const groups = React.useMemo(() => groupFlags(filteredFlags), [filteredFlags]);

  if (featureFlagsQuery.isLoading) {
    return (
      <Flex align="center" justify="center" className="py-12">
        <LoadingOrbit size="md" />
      </Flex>
    );
  }

  return (
    <Box className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <Heading level={2}>Feature Flags</Heading>
          <Text size="sm" className="text-muted-foreground">
            {flags.length === 0
              ? 'No feature flags configured'
              : `${enabledCount} of ${flags.length} ${flags.length === 1 ? 'feature' : 'features'} enabled for this organization`}
          </Text>
        </div>
        {flags.length > 0 && (
          <TextField
            aria-label="Search feature flags"
            placeholder="Search flags..."
            value={query}
            onChange={setQuery}
            leftIcon={<MagnifyingGlass size={16} />}
            className="w-64"
          />
        )}
      </div>

      {flags.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-border rounded-lg">
          <Gear size={48} className="mx-auto text-muted-foreground mb-4" />
          <Text className="text-muted-foreground">No feature flags found</Text>
        </div>
      ) : filteredFlags.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-border rounded-lg">
          <Text className="text-muted-foreground">
            No flags match &ldquo;{query}&rdquo;
          </Text>
        </div>
      ) : (
        <div className="space-y-8">
          {groups.map((group, groupIndex) => (
            <motion.section
              key={group.namespace}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2, delay: groupIndex * 0.04 }}
              className="space-y-3"
            >
              <div className="flex items-baseline justify-between">
                <Text
                  size="xs"
                  weight="semibold"
                  className="uppercase tracking-wider text-muted-foreground"
                >
                  {group.namespace}
                </Text>
                <Text size="xs" className="text-muted-foreground tabular-nums">
                  {group.flags.filter((f) => f.enabled).length} / {group.flags.length}
                </Text>
              </div>
              <div className="rounded-lg border border-border bg-card divide-y divide-border overflow-hidden">
                {group.flags.map((flag, flagIndex) => (
                  <motion.div
                    key={flag.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.15, delay: groupIndex * 0.04 + flagIndex * 0.02 }}
                    className="flex items-start justify-between gap-4 px-4 py-3.5 transition-colors hover:bg-muted/40"
                  >
                    <div className="min-w-0 flex-1">
                      <Flex align="center" gap="sm">
                        <Text weight="medium">{formatFlagLabel(flag.name)}</Text>
                        <code className="rounded bg-muted px-1.5 py-0.5 text-xs font-mono text-muted-foreground">
                          {flag.name}
                        </code>
                      </Flex>
                      {flag.description && (
                        <Text size="sm" className="text-muted-foreground mt-1">
                          {flag.description}
                        </Text>
                      )}
                    </div>
                    <Switch
                      aria-label={`Toggle ${formatFlagLabel(flag.name)}`}
                      isSelected={flag.enabled}
                      isDisabled={toggleFeatureFlagMutation.isPending}
                      onChange={() => toggleFeatureFlagMutation.mutate(flag.name)}
                    />
                  </motion.div>
                ))}
              </div>
            </motion.section>
          ))}
        </div>
      )}
    </Box>
  );
}
